<template>
  <div>
    <page-header
      :title="$t('recipes')"
      :subtitle="$t('manage_recipes')"
      icon="mdi-book-open-page-variant"
    >
      <template #actions>
        <v-btn text small color="primary" class="align-self-center mr-2" @click="$emit('new-view')">
          {{ $t("report_desktop.new_view") }}
        </v-btn>
        <template v-if="!device.isMobile">
          <circle-button
            :loading="loading.load"
            @click="load"
            icon="mdi-refresh"
            color="primary"
            :small="false"
            class="mr-2"
          />
          <global-button-print @click="print" :disabled="!items.length" />
          <global-button-excel @click="exportExcel" :disabled="!items.length" />
        </template>
      </template>
    </page-header>

    <page-toolbar :searchable="false" :loading="loading.load" v-if="!device.isMobile">
      <template #filters>
        <v-col cols="auto" lg="3" xl="2">
          <dropdown
            :label="$t('view')"
            :items="showItems"
            v-model="selectedLayout"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="auto" lg="4" xl="3">
          <date-picker
            v-model="dateRange"
            :label="$t('date')"
            :disabled="loading.load"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="auto" lg="2" xl="1">
          <autocomplete
            :label="$t('shifts')"
            v-model="selectedShifts"
            :items="availableShifts"
            multiple
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="auto" lg="3" xl="2" v-if="allMeals.length">
          <autocomplete
            :label="$t('meals')"
            v-model="selectedMeals"
            :items="allMeals"
            item-text="text"
            item-value="value"
            multiple
            select-all
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="auto" style="padding-top: 10px">
          <v-switch
            v-model="showPrice"
            hide-details
            color="primary"
            inset
            dense
            class="mt-4"
          >
            <template #label>
              <span class="mt-n3 ml-0">{{ $t('price') }}</span>
            </template>
          </v-switch>
        </v-col>
      </template>
    </page-toolbar>

    <mobile-filters
      v-if="device.isMobile"
      :filters="mobileFilterConfig"
      @update:filter="handleMobileFilterUpdate"
    />

    <div class="mt-3 px-2">
      <v-row
        v-if="loading.load"
        justify="center"
        align="center"
        style="min-height: 300px"
      >
        <v-col cols="auto">
          <v-progress-circular
            indeterminate
            size="64"
            color="primary"
          ></v-progress-circular>
        </v-col>
      </v-row>

      <empty-state
        v-else-if="!items.length"
        icon="mdi-food-off"
        :title="$t('no_data_for_selected_filters')"
        :subtitle="$t('no_recipes_for_selected_period')"
      />
      <v-row no-gutters>
        <v-col
          cols="12"
          lg="6"
          style="padding: 2px"
          v-for="(item, index) in computedTables"
          :key="`section-${index}`"
        >
          <!-- Data Table -->
          <recipe-table :item="item" :show-price="showPrice" @recalculate="recalculateItem" />
        </v-col>
      </v-row>
    </div>

    <!-- Mobile FAB Menu -->
    <v-speed-dial
      v-if="device.isMobile"
      v-model="fabMenu"
      fixed
      right
      direction="bottom"
      transition="slide-y-reverse-transition"
      style="top: 80px"
    >
      <template v-slot:activator>
        <v-btn v-model="fabMenu" color="grey" fab dark small depressed>
          <v-icon v-if="fabMenu">mdi-close</v-icon>
          <v-icon v-else>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>

      <!-- Excel Button -->
      <v-btn
        fab
        small
        color="green darken-2"
        dark
        @click="exportExcel"
        :disabled="!items.length"
      >
        <v-icon>mdi-microsoft-excel</v-icon>
      </v-btn>

      <!-- Print Button -->
      <v-btn fab small color="primary" dark @click="print" :disabled="!items.length">
        <v-icon>mdi-printer</v-icon>
      </v-btn>

      <!-- Refresh Button -->
      <v-btn fab small color="blue" dark @click="load">
        <v-icon>mdi-reload</v-icon>
      </v-btn>
    </v-speed-dial>
  </div>
</template>

<script>
import moment from "moment";
import warehouseService from "@/services/warehouse";
import companyService from "@/services/company";
import warehouseFiltersMixin from "@/mixins/warehouse/warehouseFiltersMixin";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasMobileFilters from "@/mixins/hasMobileFilters";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import GlobalButtonExcel from "@/components/global/buttons/GlobalButtonExcel.vue";
import GlobalButtonPrint from "@/components/global/buttons/GlobalButtonPrint.vue";
import MobileFilters from "@/components/global/MobileFilters.vue";
import RecipeTable from "@/components/recipes/RecipeTable.vue";

/**
 * Stara stranica Recepata (do prelaska na recipes-shell): ostaje dostupna na ?ui=stari dok se novi izgled ne potvrdi.
 * Nije mijenjana osim dugmeta "Novi izgled" u zaglavlju.
 */
export default {
  name: "RecipesLegacy",
  components: {
    PageHeader,
    PageToolbar,
    EmptyState,
    GlobalButtonExcel,
    GlobalButtonPrint,
    MobileFilters,
    RecipeTable,
  },
  mixins: [warehouseFiltersMixin, hasPrinting, hasExcel, hasMobileFilters],
  data() {
    const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10);
    return {
      fabMenu: false,
      showPrice: false,
      dateRange: [today, today],
      loading: {
        load: false,
        companies: false,
      },
      selectedLayout: null,
      availableShifts: [1, 2, 3, 4],
      selectedShifts: [1, 2, 3],
      allItems: [],
      selectedMeals: [],
      recalcVersion: 0,
    };
  },
  mounted() {
    this.loadCompanies();
  },
  methods: {
    async loadCompanies() {
      this.loading.companies = true;
      try {
        const res = await companyService.getCompanies();
        const companies = res.data || [];
        const groupCount = companies.filter((c) => c.type === "group").length;
        const layout =
          groupCount > companies.length - groupCount ? "daily-group" : "daily";
        this.selectedLayout = { text: this.$t("meals_in_meals"), value: layout };
      } catch (error) {
        console.error("Error loading companies:", error);
        this.selectedLayout = { text: this.$t("meals_in_meals"), value: "daily" };
      } finally {
        this.loading.companies = false;
      }
    },
    async load() {
      if (this.loading.load) {
        console.warn("Load operation already in progress, skipping duplicate request");
        return;
      }

      this.loading.load = true;

      try {
        let res;
        if (this.selectedLayout.value === "daily") {
          res = await warehouseService.getDailyWarehouseReport(104, {
            date_from: moment(this.dateRange[0]).format("YYYY-MM-DD"),
            date_to: moment(this.dateRange[1]).format("YYYY-MM-DD"),
            price: true,
          });
        } else {
          res = await warehouseService.getDailyGroupWarehouseReport(
            this.$store.state.restaurant.id,
            {
              date_from: moment(this.dateRange[0]).format("YYYY-MM-DD"),
              date_to: moment(this.dateRange[1]).format("YYYY-MM-DD"),
              price: true,
            }
          );
        }
        this.allItems = res.data;

        // Always reset selectedMeals to all available meals after loading
        this.selectedMeals = [...this.allItems.map((item) => item.food_name), "summary"];
      } catch (error) {
        console.error("Error loading warehouse report:", error);
      } finally {
        this.loading.load = false;
      }
    },
    recalculateItem() {
      this.recalcVersion++;
    },
    formatQuantity(value, measure) {
      if (!value || value <= 0) return "";
      return measure && measure.toLowerCase() === "kom"
        ? Math.round(value)
        : value.toFixed(3);
    },
    formatWithUnit(value, measure) {
      const formatted = this.formatQuantity(value, measure);
      return formatted !== "" ? `${formatted} ${measure}` : "";
    },
    buildShiftHeaders(shifts) {
      return [
        { text: this.$t("grocery"), value: "name", print: true },
        ...this.uniqueShifts.map((shift) => {
          const s = shifts?.find((s) => s.shift === shift);
          return {
            text: `${this.$t(`shift_${shift}`)} (${s ? s.quantity : 0})`,
            value: `shift_${shift}`,
            print: true,
          };
        }),
        { text: this.$t("total"), value: "total", print: true },
      ];
    },
    buildRecipeTables() {
      return this.computedTables
        .filter((t) => t.groceries.length)
        .map((table) => {
          const headerShifts = table.isTotal
            ? this.uniqueShifts.map((s) => ({
                shift: s,
                quantity: table.shiftTotals[s] || 0,
              }))
            : table.shifts;

          return {
            title: table.food_name,
            headers: this.buildShiftHeaders(headerShifts),
            items: table.groceries.map((gr) => {
              const row = { name: gr.name, total: gr.total };
              Object.entries(gr.shiftValues).forEach(([shift, val]) => {
                row[`shift_${shift}`] = val;
              });
              return row;
            }),
          };
        });
    },
    print() {
      this.setPrinter();
      this.printDocument({
        pageTitle: this.$t("recipes"),
        title: this.$t("recipes"),
        subtitle: `${this.dateRange[0]} - ${this.dateRange[1]}`,
        tables: this.buildRecipeTables(),
      });
    },
    exportExcel() {
      this.setExcel();
      this.downloadExcel({
        fileName: `recepti_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`,
        sheets: this.buildRecipeTables().map((t) => ({
          ...t,
          name: (t.title || "Sheet").substring(0, 31),
        })),
      });
    },
  },
  computed: {
    items() {
      let filteredItems = this.allItems;

      // Filter by selected meals
      if (this.selectedMeals?.length) {
        const mealIds = this.selectedMeals.filter((id) => id !== "summary");
        filteredItems = mealIds.length
          ? filteredItems.filter((item) => mealIds.includes(item.food_name))
          : [];
      }

      // Filter and sort shifts
      const hasShiftFilter = this.selectedShifts?.length > 0;
      return filteredItems.map((item) => ({
        ...item,
        shifts: (item.shifts || [])
          .filter((s) => !hasShiftFilter || this.selectedShifts.includes(s.shift))
          .sort((a, b) => a.shift - b.shift),
      }));
    },
    //All groceries summary across all items
    summaryGroceries() {
      const groceryMap = new Map();

      // Aggregate all groceries across all items
      this.items.forEach((item) => {
        if (item.groceries && item.groceries.length) {
          item.groceries.forEach((grocery) => {
            const key = grocery.name + "_" + grocery.measure;

            if (!groceryMap.has(key)) {
              groceryMap.set(key, {
                id: grocery.id,
                name: grocery.name,
                measure: grocery.measure,
                shifts: {},
                total: 0,
              });

              // Initialize shifts
              this.uniqueShifts.forEach((shift) => {
                groceryMap.get(key).shifts[shift] = 0;
              });
            }

            const summaryGrocery = groceryMap.get(key);

            // Add quantities for each shift
            if (item.shifts && item.shifts.length) {
              item.shifts.forEach((shift) => {
                const quantity = grocery.quantity * shift.quantity;
                summaryGrocery.shifts[shift.shift] += quantity;
                summaryGrocery.total += quantity;
              });
            }
          });
        }
      });

      return Array.from(groceryMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    computedTables() {
      void this.recalcVersion;

      const tables = this.items.map((item) => {
        const shifts = item.shifts || [];
        const totalShiftQty = shifts.reduce((sum, s) => sum + s.quantity, 0);

        return {
          food_name: item.food_name,
          isTotal: false,
          shifts,
          totalShiftQuantity: totalShiftQty || "",
          groceries: (item.groceries || []).map((gr) => ({
            id: gr.id,
            name: gr.name,
            price: gr.price || 0,
            shiftValues: Object.fromEntries(
              shifts.map((s) => [
                s.shift,
                this.formatQuantity(gr.quantity * s.quantity, gr.measure),
              ])
            ),
            totalRaw: gr.quantity * totalShiftQty,
            total: this.formatWithUnit(gr.quantity * totalShiftQty, gr.measure),
          })),
        };
      });

      if (this.selectedMeals?.includes("summary") && this.summaryGroceries.length > 0) {
        const shiftTotals = {};
        let totalQuantity = 0;

        this.uniqueShifts.forEach((shift) => {
          let total = 0;
          this.items.forEach((item) => {
            const s = item.shifts?.find((s) => s.shift === shift);
            if (s) total += s.quantity;
          });
          shiftTotals[shift] = total;
          totalQuantity += total;
        });

        tables.push({
          food_name: this.$t("total") || "UKUPNO",
          isTotal: true,
          shifts: this.uniqueShifts.map((s) => ({ shift: s, quantity: 0 })),
          shiftTotals,
          totalShiftQuantity: totalQuantity || "",
          groceries: this.summaryGroceries.map((gr) => ({
            id: gr.id,
            name: gr.name,
            price: gr.price || 0,
            shiftValues: Object.fromEntries(
              this.uniqueShifts.map((s) => [
                s,
                this.formatQuantity(gr.shifts?.[s] || 0, gr.measure),
              ])
            ),
            totalRaw: gr.total,
            total: this.formatWithUnit(gr.total, gr.measure),
          })),
        });
      }

      return tables;
    },
    uniqueShifts() {
      const shiftsSet = new Set();
      this.items.forEach((item) => {
        if (item.shifts && item.shifts.length) {
          item.shifts.forEach((shift) => {
            shiftsSet.add(shift.shift);
          });
        }
      });
      return Array.from(shiftsSet).sort((a, b) => a - b);
    },
    allMeals() {
      const mealsMap = new Map();

      // Add all regular meals
      this.allItems.forEach((item) => {
        if (item.food_name) {
          mealsMap.set(item.food_name, {
            text: this.toLatin(item.food_name),
            value: item.food_name,
          });
        }
      });

      // Add summary as a special meal option
      mealsMap.set("summary", {
        text: this.$t("total") || "UKUPNO",
        value: "summary",
      });

      const meals = Array.from(mealsMap.values()).sort((a, b) =>
        a.text.localeCompare(b.text)
      );

      return meals;
    },
    mobileFilterDefs() {
      return {
        view: {
          type: "dropdown",
          model: "selectedLayout",
          label: this.$t("view"),
          items: this.showItems,
          itemText: "text",
          itemValue: "value",
          transform: (val) => this.showItems.find((i) => i.value === val) || null,
        },
        dateRange: { type: "date", model: "dateRange", label: this.$t("date") },
        shifts: {
          type: "autocomplete",
          model: "selectedShifts",
          label: this.$t("shifts"),
          items: this.availableShifts,
          multiple: true,
        },
        meals: {
          type: "autocomplete",
          model: "selectedMeals",
          label: this.$t("meals"),
          items: this.allMeals,
          itemText: "text",
          itemValue: "value",
          multiple: true,
          visible: this.allMeals.length > 0,
        },
      };
    },
  },
  watch: {
    dateRange: {
      handler() {
        if (this.selectedLayout) {
          this.load();
        }
      },
      deep: true,
    },
    selectedLayout() {
      if (this.selectedLayout) {
        this.load();
      }
    },
  },
};
</script>
